import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useNavigate } from 'react-router-dom';
import { 
  ExternalLink, 
  BarChart3, 
  Users, 
  TrendingUp,
  Settings,
  MoreVertical,
  Plug,
  AlertCircle
} from 'lucide-react';

interface ProjectCardProps {
  project: {
    id: string;
    name: string;
    description?: string;
    websiteUrl?: string;
    industry?: string;
    isActive?: boolean;
    ga4Connected?: boolean;
    lastSync?: string | null;
    stats?: {
      sessions?: number;
      users?: number;
      conversions?: number;
      bounceRate?: number;
    };
  };
  viewMode: 'grid' | 'list';
}

export function ProjectCard({ project, viewMode }: ProjectCardProps) {
  const navigate = useNavigate();

  const formatNumber = (num: number = 0) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatBounceRate = (rate: number = 0) => {
    // GA4 returns bounce rate as a decimal (0.45 = 45%)
    const percentage = rate * 100;
    return `${percentage.toFixed(1)}%`;
  };

  // Ensure stats object exists with default values
  const stats = project.stats || {
    sessions: 0,
    users: 0,
    conversions: 0,
    bounceRate: 0
  };

  if (viewMode === 'list') {
    return (
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">{project.name}</h3>
                <Badge variant={project.isActive ? 'success' : 'secondary'}>
                  {project.isActive ? 'Active' : 'Inactive'}
                </Badge>
                {project.ga4Connected ? (
                  <Badge variant="outline" className="text-green-600 dark:text-green-400 border-green-200 dark:border-green-700">
                    GA4 Connected
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-700">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Not Connected
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{project.description}</p>
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                <span>{project.industry}</span>
                {project.websiteUrl && (
                  <a
                    href={project.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Visit site
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatNumber(stats.sessions)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sessions</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatNumber(stats.users)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Users</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{stats.conversions}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Conversions</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatBounceRate(stats.bounceRate)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Bounce Rate</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                View Details
              </Button>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{project.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{project.industry}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge variant={project.isActive ? 'success' : 'secondary'}>
            {project.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{project.description}</p>

      {/* Connection Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          {project.ga4Connected ? (
            <>
              <Plug className="w-4 h-4 text-green-600 dark:text-green-400" />
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">GA4 Connected</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">Setup Required</span>
            </>
          )}
        </div>
        
        {project.websiteUrl && (
          <a
            href={project.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            Visit site
          </a>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <Users className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-1" />
            <span className="text-lg font-semibold text-gray-900 dark:text-white">{formatNumber(stats.sessions)}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Sessions</p>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400 mr-1" />
            <span className="text-lg font-semibold text-gray-900 dark:text-white">{stats.conversions || 0}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Conversions</p>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <Users className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-1" />
            <span className="text-lg font-semibold text-gray-900 dark:text-white">{formatNumber(stats.users)}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Users</p>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <TrendingUp className="w-4 h-4 text-pink-600 dark:text-pink-400 mr-1" />
            <span className="text-lg font-semibold text-gray-900 dark:text-white">{formatBounceRate(stats.bounceRate)}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Bounce Rate</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2">
        <Button 
          size="sm" 
          className="flex-1"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/projects/${project.id}`);
          }}
        >
          View Details
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/projects/${project.id}?tab=settings`);
          }}
        >
          <Settings className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}